import React, { useState, useEffect, useRef } from "react";
import { IconButton } from "@mui/material";
import { Remove } from "@mui/icons-material";
import patientService from "../../Services/patientService";
import toast from "react-hot-toast";
import AddPurchaseModal from "../../Components/AddPurchaseModal";
import productService from "../../Services/productService";
import distributorServices from "../../Services/distributorServices";
import moment from "moment/moment";
import salesService from "../../Services/salesService";
import InvoiceSaleModal from "../../Components/InvoiceSaleModal";
import { useNavigate } from "react-router-dom";
import AutoComplete from "react-select";
import { MdPerson, MdCalendarToday, MdPointOfSale, MdDelete } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const POS = () => {
  const [patients, setPatients] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [search, setSearch] = useState("");
  const [searchDistributor, setSearchDistributor] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [purchase, setPurchase] = useState({
    customer_name: "",
    customer_age: "",
    customer_phone: "",
    amount: 0,
    date: moment().format("YYYY-MM-DD"),
    note: "",
  });
  const [products, setProducts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [medicinesOptions, setMedicinesOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const medicineRef = useRef();

  const handleSelectDistributor = (medicine) => {
    setPurchase((p) => ({ ...p, supplier_id: medicine.id }));
    setSearchDistributor(medicine.name);
    setShowDropdown(false);
  };

  const handleAddPurchase = (newProduct) => {
    setProducts([...products, newProduct]);
    setIsOpen(false);
  };

  const getMedicines = async () => {
    try {
      const response = await productService.fetchMedicines();
      setMedicines(response.products);
    } catch (error) {
      toast.error("Error fetching Medicines");
    }
  };

  const getDistributors = async () => {
    try {
      const response = await distributorServices.fetchAll();
      setDistributors(response.distributors);
    } catch (error) {
      toast.error("Error fetching Distributors");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  useEffect(() => {
    if (medicines.length > 0) {
      let array = [];
      for (const medicine of medicines) {
        array.push({
          value: medicine.stock_id,
          label: `${medicine.medicine_name} (PKR ${medicine.sale_price} - ${formatDate(medicine.expiry_date)} Qty: ${medicine.quantity})`,
        });
      }
      setMedicinesOptions(array);
    }
  }, [medicines]);

  useEffect(() => {
    getMedicines();
    getDistributors();
    medicineRef.current.focus();
  }, []);

  const handleSelectMedicine = (id) => {
    let medicine = medicines.filter((m) => m.stock_id === id)[0];
    const newProduct = {
      medicine_id: medicine.id,
      itemCode: medicine.item_code,
      itemName: medicine.medicine_name,
      expiry_date: medicine.expiry_date,
      batch_no: medicine.batch_no,
      quantity: 1,
      available_quantity: medicine.quantity,
      pack: 1,
      box: 1,
      unit_price: medicine.unit_price || 0,
      packPrice: medicine.pack_price || 0,
      discount: parseFloat(medicine.discount_price),
      sale_price: medicine.sale_price,
      discount_price: (medicine.sale_price - medicine.discount_price) * 1 || 0,
      total_price: medicine.sale_price * 1 || 0,
      stock_id: medicine.stock_id,
    };
    setProducts([...products, newProduct]);
    setSearch("");
  };

  const handleRemovePurchase = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleQuantity = (index, name, value) => {
    setProducts((products) => {
      const array = [...products];
      let q = array[index].quantity;
      let p = array[index].sale_price;
      let dp = array[index].discount;
      if (name === "quantity") {
        if (value <= 0 || isNaN(value)) {
          toast.error("Quantity must be greater than zero");
          return products;
        }
        q = value;
      } else if (name === "sale_price") {
        p = value;
      } else if (name === "discount") {
        dp = value;
      }
      array[index] = {
        ...array[index],
        [name]: value,
        discount_price: q * (p - dp),
        total_price: p * q,
      };
      return array;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!purchase.customer_name) { toast.error("Customer Name is required"); return; }
    if (!purchase.date) { toast.error("Invoice Date is required"); return; }
    if (products.length === 0) { toast.error("At least one medicine must be added"); return; }
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (Number(product.quantity) > Number(product.available_quantity)) {
        toast.error(`Quantity for "${product.itemName}" exceeds available quantity (${product.available_quantity})`);
        return;
      }
    }
    let totalAmount = 0;
    for (let product of products) totalAmount += product.discount_price;
    let submit = { ...purchase };
    submit.amount = totalAmount;
    submit.products = products;
    setLoading(true);
    salesService.create(submit).then((res) => {
      navigate("/sales");
      toast.success("Sale Created Successfully");
    }).finally(() => setLoading(false));
  };

  const getAvailableDiscounts = (salePrice, unitPrice) => {
    const profitPercentage = ((salePrice - unitPrice) / unitPrice) * 100;
    if (profitPercentage >= 15) return [0, 5, 4, 3];
    if (profitPercentage >= 11 && profitPercentage < 15) return [0, 4, 3];
    if (profitPercentage >= 8 && profitPercentage < 11) return [0, 3];
    if (profitPercentage >= 5 && profitPercentage < 8) return [0, 2];
    if (profitPercentage >= 0 && profitPercentage < 5) return [0, 1];
    return [0];
  };

  const handleDiscountChange = (index, selectedDiscount) => {
    setProducts((prevProducts) => {
      const updatedProducts = [...prevProducts];
      const product = updatedProducts[index];
      const quantity = Number(product.quantity) || 1;
      const salePrice = Number(product.sale_price) || 0;
      const discountPerUnit = (salePrice * selectedDiscount) / 100;
      const discountedUnitPrice = salePrice - discountPerUnit;
      const totalDiscount = discountPerUnit * quantity;
      const totalAfterDiscount = discountedUnitPrice * quantity;
      const totalPrice = salePrice * quantity;
      updatedProducts[index] = {
        ...product,
        discount_selected: selectedDiscount,
        discount: totalDiscount,
        discount_price: totalAfterDiscount,
        total_price: totalPrice,
      };
      return updatedProducts;
    });
  };

  const totalBeforeDiscount = products.reduce((sum, p) => sum + p.total_price, 0);
  const totalDiscount = products.reduce((sum, p) => sum + (p.discount || 0) * (p.quantity || 1), 0);
  const totalAfterDiscount = products.reduce((sum, p) => sum + (p.discount_price || 0), 0);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: "0.75rem",
      border: state.isFocused ? "1.5px solid #00A95B" : "1.5px solid #dde4ed",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(0,169,91,0.1)" : "none",
      padding: "2px 4px",
      fontSize: "14px",
      fontFamily: "'DM Sans', sans-serif",
      "&:hover": { borderColor: "#00A95B" },
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "13px",
      backgroundColor: state.isSelected ? "#00A95B" : state.isFocused ? "#f0fdf7" : "white",
      color: state.isSelected ? "white" : "#0a2540",
    }),
    placeholder: (base) => ({ ...base, color: "#b0bcc9" }),
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
        .pos-input {
          background: #f8fafc;
          border: 1.5px solid #dde4ed;
          border-radius: 0.75rem;
          padding: 10px 14px;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: #0a2540;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
          width: 100%;
        }
        .pos-input:focus {
          border-color: #00A95B;
          box-shadow: 0 0 0 4px rgba(0,169,91,0.1);
        }
        .pos-input::placeholder { color: #b0bcc9; }
        .qty-input {
          background: #f8fafc;
          border: 1.5px solid #dde4ed;
          border-radius: 0.5rem;
          padding: 6px 8px;
          font-size: 13px;
          font-family: 'DM Sans', sans-serif;
          color: #0a2540;
          outline: none;
          width: 56px;
          text-align: center;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        @media (min-width: 640px) {
          .qty-input { width: 64px; }
        }
        .qty-input:focus {
          border-color: #00A95B;
          box-shadow: 0 0 0 3px rgba(0,169,91,0.1);
        }
        .disc-select {
          background: #f8fafc;
          border: 1.5px solid #dde4ed;
          border-radius: 0.5rem;
          padding: 5px 8px;
          font-size: 12px;
          font-family: 'DM Sans', sans-serif;
          color: #0a2540;
          outline: none;
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .disc-select:focus { border-color: #00A95B; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .fade-up { animation: fadeUp 0.5s ease both; }
        /* Table needs a floor width so columns stay legible; the wrapper
           handles horizontal scroll on narrow screens instead of squishing. */
        .pos-table { min-width: 980px; }
        @media (max-width: 639px) {
          .pos-table { min-width: 860px; }
        }
      `}</style>

      <div
        className="min-h-screen bg-[#f4f7fb] py-4 sm:py-8 px-3 sm:px-4 fade-up"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        <div className="max-w-[1400px] mx-auto">

          {/* ── Page Header ── */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2.5 sm:gap-3 mb-1">
              <div
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #00A95B, #007a40)" }}
              >
                <MdPointOfSale size={16} className="text-white" />
              </div>
              <h1
                className="text-[#0a2540] text-[20px] sm:text-[26px] leading-tight"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                Record Medicine Sales
              </h1>
            </div>
            <p className="text-[#8a9bb0] text-[13px] sm:text-sm ml-10 sm:ml-12">
              Fill in customer details and add medicines to generate a sale invoice.
            </p>
          </div>

          {/* ── Customer Info Card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] p-4 sm:p-6 mb-5 sm:mb-6">
            <h2 className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080] mb-4">
              Customer Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">

              <div>
                <label className="block text-[11px] font-semibold tracking-[0.7px] uppercase text-[#4a6080] mb-1.5">
                  Customer Name
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    className="pos-input pl-9"
                    placeholder="Enter customer name"
                    onChange={(e) => setPurchase((p) => ({ ...p, customer_name: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-semibold tracking-[0.7px] uppercase text-[#4a6080] mb-1.5">
                  Invoice Date
                </label>
                <div className="relative flex items-center">
                  <input
                    type="date"
                    className="pos-input pl-9"
                    value={purchase.date}
                    onChange={(e) => setPurchase((p) => ({ ...p, date: moment(e.target.value).format("YYYY-MM-DD") }))}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ── Medicine Search Card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] p-4 sm:p-6 mb-5 sm:mb-6">
            <h2 className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080] mb-4">
              Add Medicines
            </h2>
            <div className="w-full md:w-1/2">
              <AutoComplete
                ref={medicineRef}
                styles={selectStyles}
                options={medicinesOptions}
                onChange={(v) => handleSelectMedicine(v.value)}
                placeholder="Search medicines by name..."
              />
            </div>
          </div>

          {/* ── Products Table Card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] overflow-hidden mb-5 sm:mb-6">
            <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-[#eef1f6] flex items-center justify-between gap-2">
              <h2 className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080]">
                Sale Items
              </h2>
              {products.length > 0 && (
                <span className="text-[11px] font-semibold bg-[#f0fdf7] text-[#00A95B] border border-[#c6f0de] px-2.5 py-1 rounded-full whitespace-nowrap">
                  {products.length} item{products.length !== 1 ? "s" : ""}
                </span>
              )}
            </div>

            {/* Scrolls horizontally on small screens instead of squashing columns.
                A visible scroll hint helps on touch devices where the scrollbar is hidden. */}
            <div className="overflow-x-auto hidden lg:block">
              <table className="pos-table w-full text-left" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                <thead>
                  <tr style={{ background: '#00A95B' }}>
                    {["Sr.No", "Item Code", "Item Name", "Batch No.", "Box", "Pack", "Units", "Avail. Qty", "Expiry", "Sale Price", "Disc. %", "Disc. Amt", "Price/Unit", "Total", ""].map((h) => (
                      <th key={h} className="px-2.5 sm:px-3 py-2.5 sm:py-3 text-[10.5px] sm:text-[11px] font-semibold uppercase tracking-[0.6px] text-white whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {products.length > 0 ? (
                    products.map((product, index) => {
                      const discountAmount = (product.discount || 0).toFixed(2);
                      const discountedUnitPrice = (product.discount_price / (product.quantity || 1)).toFixed(2);
                      const totalAfterDiscount = (product.discount_price || 0).toFixed(2);

                      return (
                        <tr
                          key={index}
                          className={`text-[12.5px] sm:text-[13px] border-b border-[#f0f4f8] transition-colors ${index % 2 === 0 ? "bg-white" : "bg-[#fafbfd]"} hover:bg-[#f0fdf7]`}
                        >
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 font-bold text-[#00A95B]">{index + 1}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 font-medium text-[#4a6080]">{product.itemCode}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 font-semibold text-[#0a2540] whitespace-nowrap">{product.itemName}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 text-[#4a6080]">{product.batch_no}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 text-[#4a6080]">{product.box}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 text-[#4a6080]">{product.pack}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3">
                            <input
                              value={product.quantity}
                              type="number"
                              name="quantity"
                              className="qty-input"
                              onChange={(e) => {
                                const entered = parseInt(e.target.value, 10);
                                if (entered > product.available_quantity) {
                                  toast.error("Entered quantity exceeds available quantity!");
                                } else {
                                  handleQuantity(index, e.target.name, e.target.value);
                                }
                              }}
                            />
                          </td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-[12px] font-semibold bg-orange-50 text-orange-600 border border-orange-200">
                              {product.available_quantity}
                            </span>
                          </td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 text-[#4a6080] whitespace-nowrap">{product.expiry_date}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 font-semibold text-[#0a2540]">{product.sale_price}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3">
                            <select
                              className="disc-select"
                              value={product.discount_selected || ""}
                              onChange={(e) => handleDiscountChange(index, parseFloat(e.target.value))}
                            >
                              {getAvailableDiscounts(product.sale_price, product.unit_price).map((d) => (
                                <option key={d} value={d}>{d > 0 ? `${d}%` : "0"}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 font-semibold text-emerald-600 whitespace-nowrap">Rs. {discountAmount}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 text-[#4a6080]">{discountedUnitPrice}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3 font-bold text-[#0a2540] whitespace-nowrap">Rs. {totalAfterDiscount}</td>
                          <td className="px-2.5 sm:px-3 py-2.5 sm:py-3">
                            <button
                              type="button"
                              onClick={() => handleRemovePurchase(index)}
                              className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-colors"
                            >
                              <MdDelete size={15} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="15" className="text-center py-10 sm:py-12 text-[#b0bcc9]">
                        <div className="flex flex-col items-center gap-2">
                          <MdPointOfSale size={28} className="text-[#dde4ed] sm:hidden" />
                          <MdPointOfSale size={32} className="text-[#dde4ed] hidden sm:block" />
                          <span className="text-[13px] sm:text-sm px-4 text-center">No medicines added yet. Search above to add items.</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* Mobile Cards */}
<div className="block lg:hidden p-4 space-y-4">
  {products.length > 0 ? (
    products.map((product, index) => {
      const discountAmount = (product.discount || 0).toFixed(2);
      const discountedUnitPrice = (
        product.discount_price / (product.quantity || 1)
      ).toFixed(2);

      const totalAfterDiscount = (
        product.discount_price || 0
      ).toFixed(2);

      return (
        <div
          key={index}
          className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4"
        >
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-bold text-[#0a2540] text-base">
                {product.itemName}
              </h3>

              <p className="text-xs text-gray-500">
                Code : {product.itemCode}
              </p>

              <p className="text-xs text-gray-500">
                Batch : {product.batch_no}
              </p>
            </div>

            <button
              onClick={() => handleRemovePurchase(index)}
              className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center"
            >
              <MdDelete />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4 text-sm">

            <div>
              <p className="text-gray-500">Available Qty</p>
              <span className="font-semibold">
                {product.available_quantity}
              </span>
            </div>

            <div>
              <p className="text-gray-500">Expiry</p>
              <span className="font-semibold">
                {product.expiry_date}
              </span>
            </div>

            <div>
              <p className="text-gray-500">Sale Price</p>
              <span className="font-semibold">
                Rs. {product.sale_price}
              </span>
            </div>

            <div>
              <p className="text-gray-500">Quantity</p>

              <input
                type="number"
                value={product.quantity}
                name="quantity"
                className="qty-input mt-1"
                onChange={(e) => {
                  const entered = parseInt(e.target.value, 10);

                  if (entered > product.available_quantity) {
                    toast.error(
                      "Entered quantity exceeds available quantity!"
                    );
                  } else {
                    handleQuantity(
                      index,
                      e.target.name,
                      e.target.value
                    );
                  }
                }}
              />
            </div>

            <div>
              <p className="text-gray-500">Discount %</p>

              <select
                className="disc-select mt-1"
                value={product.discount_selected || ""}
                onChange={(e) =>
                  handleDiscountChange(
                    index,
                    parseFloat(e.target.value)
                  )
                }
              >
                {getAvailableDiscounts(
                  product.sale_price,
                  product.unit_price
                ).map((d) => (
                  <option key={d} value={d}>
                    {d}%
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p className="text-gray-500">Discount</p>
              <span className="font-semibold text-green-600">
                Rs. {discountAmount}
              </span>
            </div>

            <div>
              <p className="text-gray-500">Price / Unit</p>
              <span className="font-semibold">
                Rs. {discountedUnitPrice}
              </span>
            </div>

            <div>
              <p className="text-gray-500">Total</p>
              <span className="font-bold text-[#00A95B]">
                Rs. {totalAfterDiscount}
              </span>
            </div>

          </div>
        </div>
      );
    })
  ) : (
    <div className="text-center py-10 text-gray-400">
      No medicines added yet.
    </div>
  )}
</div>
          </div>

          {/* ── Summary + Submit Card ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#eef1f6] p-4 sm:p-6">
            <h2 className="text-[12px] sm:text-[13px] font-semibold uppercase tracking-[0.8px] text-[#4a6080] mb-4 sm:mb-5">
              Sale Summary
            </h2>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
              <SummaryCard label="Total Medicines" value={products.length} unit="" color="#0a2540" bg="#f4f7fb" />
              <SummaryCard label="Total Amount" value={`Rs. ${totalBeforeDiscount.toFixed(2)}`} color="#0a2540" bg="#f4f7fb" />
              <SummaryCard label="Total Discount" value={`Rs. ${totalDiscount.toFixed(2)}`} color="#e96f1f" bg="#fff7f0" />
              <SummaryCard label="Net Payable" value={`Rs. ${totalAfterDiscount.toFixed(2)}`} color="#00A95B" bg="#f0fdf7" highlight />
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="w-full sm:w-auto flex items-center justify-center gap-2.5 px-6 sm:px-8 py-3 sm:py-3.5 rounded-xl text-white text-[14px] sm:text-[15px] font-semibold tracking-[0.3px] transition-all disabled:opacity-60 disabled:cursor-not-allowed hover:enabled:-translate-y-px"
                style={{
                  background: "linear-gradient(135deg, #00A95B 40%, #007a40 100%)",
                  boxShadow: "0 4px 20px rgba(0,169,91,0.35)",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {loading ? (
                  <>
                    <AiOutlineLoading3Quarters size={17} className="animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <MdPointOfSale size={18} />
                    Generate Sale
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <AddPurchaseModal isOpen={isOpen} onClose={() => setIsOpen(false)} onSave={handleAddPurchase} />
      <InvoiceSaleModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

/* ── Summary Card ── */
const SummaryCard = ({ label, value, color, bg, highlight }) => (
  <div
    className={`rounded-xl p-3 sm:p-4 flex flex-col gap-1 ${highlight ? "border border-[#c6f0de]" : "border border-[#eef1f6]"}`}
    style={{ background: bg }}
  >
    <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.7px] text-[#8a9bb0]">{label}</span>
    <span className="text-[16px] sm:text-[18px] font-bold" style={{ color, fontFamily: "'DM Serif Display', serif" }}>{value}</span>
  </div>
);

export default POS;